import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import icons from "../../../assets/images/constantIcons";
import images from "../../../assets/images/constantImages";
import { Link, useNavigate } from "react-router-dom";
import DeletePopup from "./DeletePopup";
import apiService, {
  formatRupees,
  handleError,
} from "../../Common/ApiCommon/ApiService";
import PayNowPlayer from "./PayNowPlayer";
import { UserContext } from "../../Layout/UserContext";
import PasswordPopup from "./PasswordPopup";
import CancelCustomer from "./CancelCustomer";

const useAddCustomer = (playerId) => {
  const [customerData, setCustomerData] = useState([]);
  const [deletePopupIndex, setDeletePopupIndex] = useState(null); // Track which row's delete popup is open
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [rowsPerPage, setRowsPerPage] = useState(50); // Track rows per page
  const [filterType, setFilterType] = useState("true");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPayNow, setShowPayNow] = useState(null);
  const { triggerNotificationRefresh } = useContext(UserContext);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(null);
  const [cancelCustomer, setCancelCustomer] = useState(null);
  const navigate = useNavigate();
  const [passwordAction, setPasswordAction] = useState(null);
  const [cancelPassword, setCancelPassword] = useState("");

  // Fetch customer data from the API
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/customer_users";
      const params = new URLSearchParams();
      if (filterType === "true") params.append("is_active", "true");
      if (filterType === "false") params.append("is_active", "false");
      if (searchQuery) params.append("search", searchQuery);

      const response = await apiService.get(`${url}?${params.toString()}`);
      setCustomerData(response.data);
      triggerNotificationRefresh();
    } catch (errors) {
      handleError(errors);
    } finally {
      setLoading(false);
    }
  }, [filterType, searchQuery]);

  useEffect(() => {
    fetchCustomers();
  }, [filterType, searchQuery]);

  useEffect(() => {
    if (playerId) {
      setShowPasswordPrompt(playerId);
    }
  }, [playerId]);

  const handleCancelPopup = (id, password = "") => {
    setCancelCustomer(id);
    setCancelPassword(password);
  };

  const handleDeletePopup = (id) => {
    setDeletePopupIndex(id);
  };
  const handlePayNow = (id) => {
    setShowPayNow(id);
  };
  const handlePasswordPrompt = (id) => {
    setShowPasswordPrompt(id);
  };

  // Columns header
  const columns = useMemo(
    () => [
      {
        Header: "Player ID",
        accessor: "customer_uniq_id",
        headerAlign: "text-center", // Override default for this column
        cellAlign: "text-center",
        Cell: ({ value }) => <div className="text-nowrap">{value}</div>,
      },
      {
        Header: "Player Name",
        accessor: "customerName",
        headerAlign: "text-left",
        cellAlign: "text-left",
        Cell: ({ row }) => (
          <span className="">{row.original.customerName}</span>
        ),
      },
      {
        Header: "Amount",
        accessor: "amount",
        headerAlign: "text-left",
        cellAlign: "text-left",
        Cell: ({ value }) => (
          <span className="text-gray-700 text-sm">{value}</span>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        headerAlign: "text-center",
        cellAlign: "text-center",
        disableSortBy: true,
        Cell: ({ value }) => (
          <p
            className={`text-xs md:text-sm font-medium flex justify-center items-center rounded-3xl w-[100px] md:w-auto px-4 py-[6px] 
            ${value === "Active"
                ? "text-purple bg-purple bg-opacity-[10%]"
                : "text-red bg-red bg-opacity-[10%]"
              }`}
          >
            {value}
          </p>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        headerAlign: "text-center",
        cellAlign: "text-center",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <div className="flex justify-start md:justify-center items-center gap-1 md:gap-5">
              <div
                className="flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full p-2 bg-green50 text-green cursor-pointer"
                onClick={() => {
                  setPasswordAction("view");
                  handlePasswordPrompt(row.original.id);
                }}
              >
                <icons.IoEyeOutline className="text-xl font-bold" />
              </div>

              {showPasswordPrompt === row.original.id && (
                <PasswordPopup
                  isOpen={showPasswordPrompt === row.original.id}
                  onClose={() => {
                    setShowPasswordPrompt(null);
                    setPasswordAction(null);
                  }}
                  fetchCustomers={fetchCustomers}
                  customerId={row.original.id}
                  customerName={row.original.customerName}
                  passwordAction={passwordAction}
                  onPasswordSuccess={(enteredPassword) => {
                    setShowPasswordPrompt(null);
                    if (passwordAction === "delete") {
                      handleCancelPopup(row.original.id, enteredPassword)
                    } else if (passwordAction === "view") {
                      navigate(`/mainDashboard/customer/viewCustomer/${row.original.id}`, {
                        state: { password: enteredPassword },
                      });
                    }
                    setPasswordAction(null);
                  }}
                />
              )}
              <div
                className="flex items-center justify-center focus:outline-none  rounded-full p-2 cursor-pointer w-[50px]"
                onClick={() => {
                  handleDeletePopup(row.original.id, row.original.status);
                }}
              >
                {row.original.status === "Active" ? (
                  <img src={images.Active} alt="Active" className="" />
                ) : (
                  <img src={images.Deactive} alt="Deactive" className="" />
                )}
              </div>

              {deletePopupIndex === row.original.id && (
                <DeletePopup
                  isOpen={deletePopupIndex === row.original.id}
                  onClose={() => handleDeletePopup(null)}
                  customerName={row.original.customerName}
                  customerId={row.original.id}
                  fetchCustomers={fetchCustomers}
                  isActive={
                    customerData.find(
                      (customer) => customer.id === row.original.id
                    )?.status === "activated"
                  }
                />
              )}
              {/* delete password protected */}

              <div
                className="flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full p-2 bg-red bg-opacity-15 text-red cursor-pointer"
                onClick={() => {
                  setPasswordAction("delete");
                  handlePasswordPrompt(row.original.id)
                }}
              >
                <icons.AiOutlineDelete className="text-xl font-bold" />
              </div>

              {cancelCustomer === row.original.id && (
                <CancelCustomer
                  isOpen={cancelCustomer === row.original.id}
                  onClose={() => {
      handleCancelPopup(null);
      setCancelPassword("");
    }}
                  customerName={row.original.customerName}
                  customerId={row.original.id}
                  fetchCustomers={fetchCustomers}
                  password={cancelPassword}
                />
              )}
            </div>
          );
        },
      },
      {
        Header: "",
        accessor: "actio",
        headerAlign: "text-left",
        cellAlign: "text-left",
        disableSortBy: true,
        Cell: ({ row }) => {
          const amountValue =
            Number(row.original.amount.replace(/[^\d.-]/g, "")) || 0;
          const isAddAmount = amountValue <= 0;
          return (
            <div className="flex justify-start md:justify-center items-center gap-1 md:gap-5">
              <button
                className={`text-white text-center rounded-[32px] w-[120px] px-4 py-2 
    ${isAddAmount ? "bg-checkoutblue" : "bg-orange"} cursor-pointer`}
                onClick={() => handlePayNow(row.original.id)}
              >
                {isAddAmount ? "Add Amount" : "Pay Now"}
              </button>
              {showPayNow === row.original.id && (
                <PayNowPlayer
                  isOpen={showPayNow === row.original.id}
                  onClose={() => handlePayNow(null)}
                  fetchCustomers={fetchCustomers}
                  dueamount={amountValue}
                  customerId={row.original.id}
                  customerName={row.original.customerName}
                />
              )}
            </div>
          );
        },
      },
    ],
    [deletePopupIndex, customerData, showPayNow, showPasswordPrompt, cancelCustomer]
  );

  // Data of customer
  const data = useMemo(() => {
    if (!Array.isArray(customerData)) return []; // Safeguard in case customers is not an array
    return customerData?.map((customer) => ({
      id: customer.id,
      status: customer.status === "activated" ? "Active" : "Deactive",
      customer_uniq_id: customer.customer_uniq_id || "",
      customerName: customer.full_name,
      city: customer.city,
      amount: formatRupees(customer.amount) || "₹ 0",
      action: "",
      due_limit: formatRupees(customer.due_limit) || "₹ 0",
    }));
  }, [customerData]);

  // Paginate the data
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to page 1 when rows per page change
  };

  return {
    columns,
    currentRows,
    totalRows: data.length,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    fetchCustomers,
    setFilterType,
    filterType,
    searchQuery,
    setSearchQuery,
    loading,
  };
};

export default useAddCustomer;
